import type {
  EnvironmentId,
  EnvironmentTransferProgress,
  ProjectWriteFileInput,
  ProjectWriteFileResult,
} from "@cmmd-forge/contracts";
import { useEffect, useRef, useState } from "react";

import { readEnvironmentApi } from "~/environmentApi";

export type WorkspaceFileWriteState =
  | { readonly status: "idle" }
  | ({ readonly status: "saving" } & EnvironmentTransferProgress)
  | { readonly status: "saved"; readonly relativePath: string }
  | { readonly status: "cancelled" }
  | { readonly status: "error"; readonly message: string };

export function isWorkspaceFileWriteCancelled(error: unknown): boolean {
  return (
    typeof error === "object" && error !== null && "name" in error && error.name === "AbortError"
  );
}

export function useWorkspaceFileWrite(environmentId: EnvironmentId) {
  const controllerRef = useRef<AbortController | null>(null);
  const [state, setState] = useState<WorkspaceFileWriteState>({ status: "idle" });

  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    [],
  );

  const save = async (input: ProjectWriteFileInput): Promise<ProjectWriteFileResult> => {
    controllerRef.current?.abort();
    const api = readEnvironmentApi(environmentId);
    if (!api) throw new Error("The runtime disconnected before the file could be saved.");

    const controller = new AbortController();
    controllerRef.current = controller;
    const totalBytes = new TextEncoder().encode(input.contents).byteLength;
    setState({ status: "saving", completedBytes: 0, totalBytes });
    try {
      const result = await api.projects.writeFile(input, {
        signal: controller.signal,
        onProgress: (progress) => {
          if (controllerRef.current === controller) {
            setState({ status: "saving", ...progress });
          }
        },
      });
      if (controllerRef.current === controller) {
        setState({ status: "saved", relativePath: result.relativePath });
      }
      return result;
    } catch (error) {
      if (controllerRef.current === controller) {
        setState(
          isWorkspaceFileWriteCancelled(error)
            ? { status: "cancelled" }
            : {
                status: "error",
                message: error instanceof Error ? error.message : "The file could not be saved.",
              },
        );
      }
      throw error;
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
    }
  };

  return {
    state,
    save,
    cancel: () => controllerRef.current?.abort(),
    reset: () => setState({ status: "idle" }),
  } as const;
}
